import config from "../config";
import { createApi } from "@reduxjs/toolkit/query/react";
import { User, Attorney } from "../../../api/src/types";
import {
  InviteUserRequest,
  type PartnerReminderResponse,
} from "../../../api/src/users/route";
import { PartnerReminderReason } from "../../../api/src/services/email-marketing/kit/utils/utils";
import { prepareHeaders } from "./utils";
import { questionnaireApi } from "./questionnaire";
import { documentsApi } from "./documents";
import {
  createSerializedFetchBaseQuery,
  withHeader,
  SERIALIZE_BY_HEADER,
} from "./serializedFetchBaseQuery";
import { AttorneyListGetResponse } from "../../../api/src/users/attorney-services/route";
import { AttorneyServicesPurchaseEligibilityGetResponse } from "../../../api/src/users/products/attorney-services/route";
import {
  UserAdminGetResponse,
  UsersAdminGetResponse,
  UserAdminPatchRequest,
  UserAdminPatchResponse,
  UserPaymentStatusPatchRequest,
  UserPaymentStatusPatchResponse,
  UserUnsubscribeResponse,
  UserDeleteResponse,
  NotifyAttorneyUserEventResponse,
  SendPasswordResetEmailResponse,
  UsersAdminGetDuplicateDocumentsResponse,
  UsersAdminPostResolveDuplicateDocumentsResponse,
  UserAdminGetAnswerRestrictionsResponse,
  AdminQuestionnaireSectionsGetResponse,
  AdminQuestionnaireSectionGroupsGetResponse,
} from "../../../api/src/admin/users/route";
import type {
  CalendlyBypassEventRequest,
  CalendlyBypassEventResponse,
} from "../../../api/src/admin/calendly-bypass/route";
import { CreateUserRequest } from "../../../api/src/users/route";
import {
  ChatsGetResponse,
  ChatsPostResponse,
  ChatPostRequest,
} from "../../../api/src/users/chats/route";
import type { PaginatedConversationsGetResponse } from "../../../api/src/users/chats/conversations/route";

interface CreateUserAttorneyResponse {
  user: User;
  attorney: Attorney;
}

export const usersApi = createApi({
  reducerPath: "usersApi",
  baseQuery: createSerializedFetchBaseQuery({
    baseUrl: config.api,
    prepareHeaders,
  }),
  tagTypes: [
    "User",
    "QuestionnaireSections",
    "QuestionGroups",
    "Attorney",
    "AttorneyServices",
    "Clients",
    "AdminUsers",
    "Chats",
  ],
  endpoints: (builder) => ({
    createUser: builder.mutation<User, CreateUserRequest>({
      query: (userData: CreateUserRequest) => ({
        url: "users",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["User"],
    }),
    getUserById: builder.query<User, string>({
      query: (id) => ({
        url: `users/${id}`,
        method: "GET",
      }),
      providesTags: ["User"],
    }),
    getCurrentUser: builder.query<User, void>({
      query: () => ({
        url: "users/me",
        method: "GET",
      }),
      providesTags: ["User"],
    }),
    updateUser: builder.mutation<
      User,
      Partial<User> & { primaryEmailAddressId?: string }
    >({
      query: (userData) => ({
        url: `users/${userData.id}`,
        method: "PATCH",
        body: userData,
      }),

      onQueryStarted: async (
        { id, ...patch },
        // eslint-disable-next-line @typescript-eslint/unbound-method
        { dispatch, queryFulfilled, getState },
      ) => {
        const state = getState();
        const currentUser = state.usersApi.queries["getCurrentUser(undefined)"]
          ?.data as User | undefined;

        // Remove birthdate from patch to avoid serialization issues with Date objects
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { birthdate: _birthdate, ...serializablePatch } = patch;

        const patchResult =
          !id || id === currentUser?.id
            ? dispatch(
                usersApi.util.updateQueryData(
                  "getCurrentUser",
                  undefined,
                  (draft) => {
                    Object.assign(draft, serializablePatch);
                  },
                ),
              )
            : dispatch(
                usersApi.util.updateQueryData("getUserById", id, (draft) => {
                  Object.assign(draft, serializablePatch);
                }),
              );
        try {
          await queryFulfilled;
          dispatch(
            questionnaireApi.util.invalidateTags(["QuestionnaireSections"]),
          );
          dispatch(documentsApi.util.invalidateTags(["regeneration-status"]));
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ["User"],
    }),
    getAttorneyServicesPurchaseEligibility: builder.query<
      AttorneyServicesPurchaseEligibilityGetResponse,
      { userId: string; documentId: string }
    >({
      query: ({ userId, documentId }) => ({
        url: `users/${userId}/products/attorney-services/eligibility`,
        method: "GET",
        params: { documentId },
      }),
    }),
    createUserAttorney: builder.mutation<
      CreateUserAttorneyResponse,
      CreateUserRequest
    >({
      query: (userData) => ({
        url: "users",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["User", "Attorney"],
    }),
    createUserInvite: builder.mutation<
      User,
      InviteUserRequest & { userId: string; documentId: string }
    >({
      query: ({ userId, documentId, ...userData }) => ({
        url: `users/${userId}/documents/${documentId}/invite`,
        method: "POST",
        body: userData,
      }),
      onQueryStarted: async (_data, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(documentsApi.util.invalidateTags(["invites", "documents"]));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    getAttorneyList: builder.query<
      AttorneyListGetResponse,
      { userId: string; documentId: string }
    >({
      query: ({ userId, documentId }) => {
        const baseUrl = `users/${userId}/documents/${documentId}/attorneys`;

        return {
          url: baseUrl,
          method: "GET",
        };
      },
      providesTags: ["AttorneyServices"],
    }),
    sendPartnerReminder: builder.mutation<
      PartnerReminderResponse,
      {
        userId: string;
        documentId: string;
        /**
         * Cause that surfaced the "Send reminder email" CTA. Each
         * reason maps to a different Kit email template on the
         * backend, so the partner gets a reminder that actually
         * matches what's blocking the customer.
         */
        reason: PartnerReminderReason;
      }
    >({
      query: ({ userId, documentId, reason }) => ({
        url: `users/${userId}/partner-reminder`,
        method: "POST",
        body: { documentId, reason },
      }),
    }),
    getUsersAdmin: builder.query<UsersAdminGetResponse, { search?: string }>({
      query: ({ search }) => ({
        url: `admin/users`,
        method: "GET",
        params: {
          search,
        },
      }),
      providesTags: ["AdminUsers"],
    }),
    getUserAdmin: builder.query<UserAdminGetResponse, { userId: string }>({
      query: ({ userId }) => ({
        url: `admin/users/${userId}`,
        method: "GET",
      }),
      providesTags: ["AdminUsers"],
    }),
    getAdminQuestionnaireSections: builder.query<
      AdminQuestionnaireSectionsGetResponse,
      { userId: string; documentId: string }
    >({
      query: ({ userId, documentId }) => ({
        url: `admin/users/${userId}/questionnaire-sections`,
        params: { documentId },
        method: "GET",
      }),
      providesTags: ["QuestionnaireSections"],
    }),
    getAdminQuestionnaireSectionGroups: builder.query<
      AdminQuestionnaireSectionGroupsGetResponse,
      { userId: string; sectionId: string; documentId: string }
    >({
      query: ({ userId, sectionId, documentId }) => ({
        url: `admin/users/${userId}/questionnaire-sections/${sectionId}/groups`,
        params: { documentId },
        method: "GET",
      }),
      providesTags: (_result, _error, { userId }) => [
        "QuestionnaireSections",
        { type: "QuestionGroups", id: userId },
      ],
    }),
    updateUserAdmin: builder.mutation<
      UserAdminPatchResponse,
      UserAdminPatchRequest & { userId: string }
    >({
      query: ({ userId, ...data }) => ({
        url: `admin/users/${userId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["AdminUsers"],
    }),
    updateUserMarkAsPaid: builder.mutation<
      UserPaymentStatusPatchResponse,
      UserPaymentStatusPatchRequest & { userId: string }
    >({
      query: ({ userId, ...data }) => ({
        url: `admin/users/${userId}/mark-paid`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["AdminUsers"],
    }),
    adminCalendlyBypassEvent: builder.mutation<
      CalendlyBypassEventResponse,
      CalendlyBypassEventRequest
    >({
      query: (body) => ({
        url: "admin/calendly-bypass/events",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminUsers", "Clients"],
    }),
    unsubscribeUser: builder.mutation<
      UserUnsubscribeResponse,
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: `admin/users/${userId}/unsubscribe`,
        method: "POST",
      }),
    }),
    deleteUser: builder.mutation<UserDeleteResponse, { userId: string }>({
      query: ({ userId }) => ({
        url: `admin/users/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminUsers"],
    }),
    notifyAttorneyUserEvent: builder.mutation<
      NotifyAttorneyUserEventResponse,
      { userId: string; eventId: string }
    >({
      query: ({ userId, eventId }) => ({
        url: `admin/users/${userId}/notify-attorney-user-event/${eventId}`,
        method: "POST",
      }),
    }),
    // Duplicate documents detection for Admin -> Users page
    getDuplicateDocuments: builder.query<
      UsersAdminGetDuplicateDocumentsResponse,
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: `admin/users/${userId}/duplicate-documents`,
        method: "GET",
      }),
      providesTags: ["AdminUsers"],
    }),

    resolveDuplicateDocuments: builder.mutation<
      UsersAdminPostResolveDuplicateDocumentsResponse,
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: `admin/users/${userId}/resolve-duplicate-documents`,
        method: "POST",
      }),
      invalidatesTags: ["AdminUsers"],
    }),
    // Separate mutation for profile updates to avoid triggering re-renders in components that use useGetCurrentUserQuery
    // This prevents the entire questionnaire page from refreshing when user profile data is updated
    updateUserProfile: builder.mutation<
      User,
      {
        userId: string;
        fieldName: string;
        fieldValue: string | Date | null;
        debounceMs?: number | null;
      }
    >({
      query: ({ userId, fieldName, fieldValue, debounceMs }) => ({
        url: `users/${userId}`,
        method: "PATCH",
        body: { [fieldName]: fieldValue },
        headers: (() => {
          let headers = withHeader(
            undefined,
            SERIALIZE_BY_HEADER,
            `user:${userId}`,
          );
          headers = withHeader(
            headers,
            "X-Prune-By",
            `user:${userId}:${fieldName}:PATCH`,
          );
          if (debounceMs != null) {
            headers = withHeader(
              headers,
              "X-Serialize-Debounce-Ms",
              String(debounceMs),
            );
          }
          return headers;
        })(),
      }),
      onQueryStarted: async (
        { fieldName, fieldValue },
        { dispatch, queryFulfilled },
      ) => {
        // Handle birthdate properly by converting to string if it's a Date object
        const serializableFieldValue =
          fieldName === "birthdate" && fieldValue instanceof Date
            ? fieldValue.toISOString()
            : fieldValue;

        dispatch(
          usersApi.util.updateQueryData(
            "getCurrentUser",
            undefined,
            (draft) => {
              Object.assign(draft, { [fieldName]: serializableFieldValue });
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          // Collapse cache to "no value provided" on failure (do not roll back to prior values).
          dispatch(
            usersApi.util.updateQueryData(
              "getCurrentUser",
              undefined,
              (draft) => {
                Object.assign(draft, { [fieldName]: null });
              },
            ),
          );
          console.error(error);
        } finally {
          dispatch(
            questionnaireApi.util.invalidateTags(["QuestionnaireSections"]),
          );
          dispatch(documentsApi.util.invalidateTags(["regeneration-status"]));
        }
      },
    }),

    getChats: builder.query<
      ChatsGetResponse,
      { userId: string; documentId: string; after?: string }
    >({
      query: ({ userId, documentId, after }) => ({
        url: `users/${userId}/documents/${documentId}/chats`,
        method: "GET",
        params: { after },
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        const { userId, documentId } = queryArgs;
        return { userId, documentId };
      },
      providesTags: (_result, _error, { documentId }) => [
        { type: "Chats", id: documentId },
      ],
    }),
    createChatMessage: builder.mutation<
      ChatsPostResponse,
      ChatPostRequest & { userId: string; documentId: string }
    >({
      query: ({ userId, documentId, ...data }) => ({
        url: `users/${userId}/documents/${documentId}/chats`,
        method: "POST",
        body: data,
      }),
      async onQueryStarted(
        { userId, documentId, message, toUserId },
        { dispatch, queryFulfilled },
      ) {
        // Create the optimistic message object
        const optimisticMessage = {
          id: `temp-${Date.now()}-${Math.random()}`,
          toUserId,
          fromUserId: userId,
          documentId,
          message,
          readAt: null,
          createdAt: new Date().toISOString(),
        };

        // Get the cache update utility
        const patchResult = dispatch(
          usersApi.util.updateQueryData(
            "getChats",
            { userId, documentId },
            (draft) => {
              // Initialize the array if it doesn't exist (for empty chats)
              draft[toUserId] = draft[toUserId] ?? [];
              draft[toUserId].push(optimisticMessage);
            },
          ),
        );

        try {
          await queryFulfilled; // Wait for the API call to complete
        } catch {
          patchResult.undo(); // If it fails, RTK Query automatically reverts the optimistic update!
          // You can also dispatch an action here to show an error toast
        }
      },
      invalidatesTags: (_result, _error, { documentId }) => [
        { type: "Chats", id: documentId },
        "Chats",
      ],
    }),
    markChatsAsRead: builder.mutation<
      { message: string; updatedCount: number },
      { userId: string; documentId: string; chatIds: string[] }
    >({
      query: ({ userId, documentId, chatIds }) => ({
        url: `users/${userId}/documents/${documentId}/chats/read`,
        method: "PATCH",
        body: { chatIds },
      }),
      invalidatesTags: ["Chats"],
    }),
    deleteAllChats: builder.mutation<
      { deletedCount: number },
      { userId: string; documentId: string }
    >({
      query: ({ userId, documentId }) => ({
        url: `users/${userId}/documents/${documentId}/chats`,
        method: "DELETE",
      }),
    }),
    sendPasswordResetEmail: builder.mutation<
      SendPasswordResetEmailResponse,
      { userId: string; documentId: string }
    >({
      query: ({ userId, documentId }) => ({
        url: `admin/users/${userId}/send-password-reset-email`,
        method: "POST",
        params: { documentId },
      }),
    }),
    getConversations: builder.query<
      PaginatedConversationsGetResponse,
      { userId: string; offset?: number; limit?: number }
    >({
      query: ({ userId, offset, limit }) => ({
        url: `users/${userId}/chats/conversations`,
        method: "GET",
        params: { offset, limit },
      }),
      providesTags: ["Chats"],
    }),
    getUserAnswerRestrictionsAdmin: builder.query<
      UserAdminGetAnswerRestrictionsResponse,
      { userId: string; documentId: string }
    >({
      query: ({ userId, documentId }) => ({
        url: `admin/users/${userId}/attorney-restrictions`,
        method: "GET",
        params: { documentId },
      }),
      providesTags: ["AdminUsers"],
    }),
  }),
});

export const {
  useCreateUserMutation,
  useGetUserByIdQuery,
  useUpdateUserMutation,
  useUpdateUserProfileMutation,
  useGetCurrentUserQuery,
  useGetAttorneyServicesPurchaseEligibilityQuery,
  useCreateUserAttorneyMutation,
  useCreateUserInviteMutation,
  useGetAttorneyListQuery,
  useSendPartnerReminderMutation,
  useGetUsersAdminQuery,
  useGetUserAdminQuery,
  useUpdateUserAdminMutation,
  useUpdateUserMarkAsPaidMutation,
  useAdminCalendlyBypassEventMutation,
  useUnsubscribeUserMutation,
  useDeleteUserMutation,
  useNotifyAttorneyUserEventMutation,
  useGetDuplicateDocumentsQuery,
  useResolveDuplicateDocumentsMutation,
  useGetChatsQuery,
  useCreateChatMessageMutation,
  useMarkChatsAsReadMutation,
  useDeleteAllChatsMutation,
  useGetConversationsQuery,
  useLazyGetConversationsQuery,
  useSendPasswordResetEmailMutation,
  useGetUserAnswerRestrictionsAdminQuery,
  useGetAdminQuestionnaireSectionsQuery,
  useGetAdminQuestionnaireSectionGroupsQuery,
} = usersApi;
